/**
 * 注入治療カテゴリ（BOTOX / ヒアルロン酸）の正式コンテンツデータ。
 * NOTE: 製剤名・メーカー名・単位数・注入量・濃度・持続期間・効果発現までの日数・保証・料金・症例数は
 *       未確認のため作成しない。未確定の項目は「—」とし、確定後に追記できる構造にしている。
 *       「しわが消える」「小顔になる」「鼻が高くなる」「若返る」などの効果断定表現は使用しない。
 *       METHOD（施術情報）は分類・施術方法のみを表示する（使用製剤名は現時点では掲載しない）。
 *       料金は公式サイトの定価（1回）を掲載する。モニター価格は掲載しない。
 */

export type InjectablesAreaGroup = {
  en?: string;
  jp: string;
  items: string[];
};

export type InjectablesFeature = { no: string; title: string; text: string };
export type InjectablesStep = { no: string; title: string; text: string };
export type InjectablesSpec = { label: string; value: string };
export type InjectablesPriceRow = { label: string; price: string };
export type InjectablesFaq = { q: string; a: string };
export type InjectablesRelated = { label: string; to: string };

export type InjectablesDetail = {
  slug: "botox" | "hyaluronic-acid";
  path: string;
  priceTab: string;
  en: string;
  jp: string;
  heroMain: string;
  heroSub: string;
  /** FV画像。ページごとに1対1で差し替え可能（注入治療＝トープ/チャコールベージュ系） */
  heroImage: string;
  /** FV画像の object-position（人物の見え方をページごとに調整） */
  heroObjectPosition?: string;
  heroImageAlt: string;
  heroNote: string;
  aboutEn: string;
  aboutJp: string;
  aboutParagraphs: string[];
  areasEn: string;
  areasJp: string;
  areasLead?: string;
  areaGroups: InjectablesAreaGroup[];
  recommendedEn: string;
  recommendedJp: string;
  recommendedLead?: string;
  recommended: string[];
  featuresEn: string;
  featuresJp: string;
  features: InjectablesFeature[];
  /** 施術情報（METHOD）。分類・施術方法の2項目をストリップ形式で表示する。 */
  method: InjectablesSpec[];
  /** 公式サイトの定価（1回）。 */
  price: InjectablesPriceRow[];
  flow: InjectablesStep[];
  downtimeEn: string;
  downtimeJp: string;
  downtime: InjectablesSpec[];
  riskEn: string;
  riskJp: string;
  riskParagraphs: string[];
  faqs: InjectablesFaq[];
  related: InjectablesRelated[];
  ctaHeading: string;
  ctaLineLabel: string;
};

const flow: InjectablesStep[] = [
  { no: "01", title: "ご予約", text: "WEB予約・LINE・お電話より、ご希望の日時をお知らせください。" },
  { no: "02", title: "カウンセリング", text: "気になる部位やご希望、生活スタイルを丁寧にお伺いします。" },
  { no: "03", title: "医師の診察", text: "表情や顔立ち、状態を確認し、治療の適応を判断します。" },
  {
    no: "04",
    title: "治療内容・料金の説明",
    text: "施術内容、料金、リスク、ダウンタイムなどをご説明します。",
  },
  { no: "05", title: "施術", text: "内容にご同意いただいたうえで、施術を行います。" },
  { no: "06", title: "アフターケア", text: "施術後の経過を確認し、注意事項をご案内します。" },
];

const downtime: InjectablesSpec[] = [
  { label: "施術時間", value: "—" },
  { label: "麻酔", value: "—" },
  { label: "赤み", value: "—" },
  { label: "腫れ", value: "—" },
  { label: "痛み", value: "—" },
  { label: "内出血", value: "—" },
  { label: "メイク", value: "—" },
  { label: "入浴", value: "—" },
  { label: "運動", value: "—" },
];

const riskParagraphs: string[] = [
  "注射による治療のため、施術部位に赤み、腫れ、痛み、内出血などが生じる場合があります。",
  "治療に伴うリスク・副作用・治療を受けられない場合については、診察時に医師よりご説明します。",
];

const PRICE_TAB = "injectables";

export const injectablesDetails: InjectablesDetail[] = [
  /* ---------------------------------- BOTOX ---------------------------------- */
  {
    slug: "botox",
    path: "/injectables/botox",
    priceTab: PRICE_TAB,
    en: "BOTOX",
    jp: "ボトックス",
    heroMain: "表情や筋肉の動きに合わせた、繊細な注入治療。",
    heroSub: "表情や筋肉の動き、気になる部位を確認し、医師の診察のもと治療をご提案します。",
    heroImage:
      "/images/site/injectables-botox-fv.webp",
    heroObjectPosition: "object-[60%_center]",
    heroImageAlt: "ボトックスの施術イメージ",
    heroNote: "施術画像は準備中です",
    aboutEn: "ABOUT TREATMENT",
    aboutJp: "ボトックスとは",
    aboutParagraphs: [
      "ボトックスは、顔や身体の気になる部位についてご相談いただける注入治療の一つです。",
      "SBDCでは、表情や筋肉の動き、顔立ち、治療部位などを確認しながら、医師が治療の適応を判断します。",
      "使用する製剤、単位数、注入量などについては、診察内容に応じてご案内します。",
    ],
    areasEn: "TREATMENT AREAS",
    areasJp: "施術部位",
    areasLead: "対応部位をご案内します。施術の適応や実際に用いる内容は、診察時にご案内します。",
    areaGroups: [
      {
        en: "FACE",
        jp: "顔",
        items: ["額", "眉間", "バニー", "目尻", "たれ目", "人中", "口角", "ガミー", "アゴ"],
      },
      {
        en: "BODY",
        jp: "身体",
        items: ["エラ", "ワキ", "肩", "ふくらはぎ"],
      },
    ],
    recommendedEn: "RECOMMENDED FOR",
    recommendedJp: "このようなお悩みについてご相談いただけます",
    recommended: [
      "表情によるしわが気になる",
      "顔の印象について相談したい",
      "エラについて相談したい",
      "肩やふくらはぎなど身体の部位について相談したい",
      "ワキの治療について相談したい",
      "自分に合った注入治療を知りたい",
    ],
    featuresEn: "FEATURES",
    featuresJp: "特徴",
    features: [
      { no: "01", title: "表情や筋肉の動きを確認", text: "表情や筋肉の動きを確認し、気になる部位についてご相談いただけます。" },
      { no: "02", title: "医師の診察のもと治療", text: "医師の診察のもと、治療の適応を判断します。" },
      { no: "03", title: "部位ごとに治療内容を検討", text: "施術部位ごとに、治療内容を検討します。" },
      { no: "04", title: "一人ひとりに合わせてご提案", text: "お悩みやご希望、顔立ちに合わせてご提案します。" },
    ],
    method: [
      { label: "分類", value: "INJECTABLES" },
      { label: "施術方法", value: "注入治療" },
    ],
    price: [
      { label: "額（10単位）", price: "33,000円" },
      { label: "眉間（10単位）", price: "33,000円" },
      { label: "バニー（10単位）", price: "33,000円" },
      { label: "目尻（10単位）", price: "33,000円" },
      { label: "たれ目（10単位）", price: "33,000円" },
      { label: "人中（10単位）", price: "33,000円" },
      { label: "口角（10単位）", price: "33,000円" },
      { label: "ガミー（10単位）", price: "33,000円" },
      { label: "アゴ（10単位）", price: "33,000円" },
      { label: "エラ（60単位）", price: "88,000円" },
      { label: "ワキ（両ワキ60単位）", price: "88,000円" },
      { label: "肩（60単位）", price: "88,000円" },
      { label: "ふくらはぎ（50単位）", price: "132,000円" },
    ],
    flow,
    downtimeEn: "DOWNTIME",
    downtimeJp: "ダウンタイム",
    downtime,
    riskEn: "RISK / PRECAUTIONS",
    riskJp: "リスク・注意事項",
    riskParagraphs,
    faqs: [
      {
        q: "どの部位に施術できますか？",
        a: "本ページに掲載している部位を中心にご案内しています。実際の適応は診察時にご確認ください。",
      },
      {
        q: "何単位使用しますか？",
        a: "使用量は施術部位や診察内容によって異なります。診察時にご案内します。",
      },
      {
        q: "効果はどのくらい続きますか？",
        a: "治療後の経過には個人差があります。詳しくは診察時にご説明します。",
      },
      {
        q: "当日に施術できますか？",
        a: "ご予約状況や診察内容によって異なります。まずはご相談ください。",
      },
      {
        q: "他の注入治療と相談できますか？",
        a: "他の注入治療との組み合わせについても、診察時にご相談いただけます。",
      },
    ],
    related: [
      { label: "注入治療について", to: "/treatment/injectables" },
      { label: "ヒアルロン酸について", to: "/injectables/hyaluronic-acid" },
      { label: "注入治療の料金を見る", to: "/price?tab=injectables" },
    ],
    ctaHeading: "ボトックスについて、まずはご相談ください。",
    ctaLineLabel: "ボトックスについてLINEで相談",
  },

  /* ------------------------------ HYALURONIC ACID ------------------------------ */
  {
    slug: "hyaluronic-acid",
    path: "/injectables/hyaluronic-acid",
    priceTab: PRICE_TAB,
    en: "HYALURONIC ACID",
    jp: "ヒアルロン酸",
    heroMain: "顔立ちのバランスを見ながら、自然な変化を。",
    heroSub: "顔立ちやボリューム、輪郭、気になる部位を確認し、医師の診察のもと治療をご提案します。",
    heroImage:
      "/images/site/injectables-hyaluronic-acid-fv.webp",
    heroObjectPosition: "object-[64%_center]",
    heroImageAlt: "ヒアルロン酸の施術イメージ",
    heroNote: "施術画像は準備中です",
    aboutEn: "ABOUT TREATMENT",
    aboutJp: "ヒアルロン酸注入とは",
    aboutParagraphs: [
      "ヒアルロン酸注入は、顔のボリュームや輪郭、バランスなどについてご相談いただける注入治療です。",
      "SBDCでは、顔立ちや表情、気になる部位、ご希望を確認しながら、医師が治療内容を判断します。",
      "使用する製剤や注入量などについては、診察内容に応じてご案内します。",
    ],
    areasEn: "TREATMENT AREAS",
    areasJp: "施術部位",
    areasLead: "対応部位をご案内します。施術の適応や実際に用いる内容は、診察時にご案内します。",
    areaGroups: [
      {
        jp: "",
        items: ["涙袋", "鼻", "唇", "顎", "首のしわ", "ほうれい線", "口角", "小じわ", "額", "こめかみ"],
      },
    ],
    recommendedEn: "RECOMMENDED FOR",
    recommendedJp: "このようなお悩みについてご相談いただけます",
    recommended: [
      "涙袋について相談したい",
      "鼻や顎のバランスが気になる",
      "唇について相談したい",
      "額やこめかみのボリュームが気になる",
      "ほうれい線や口元について相談したい",
      "顔全体のバランスを見ながら治療したい",
    ],
    featuresEn: "FEATURES",
    featuresJp: "特徴",
    features: [
      { no: "01", title: "顔全体のバランスを確認", text: "顔立ちや表情、輪郭を確認しながらご相談いただけます。" },
      { no: "02", title: "部位ごとに注入方法を検討", text: "施術部位ごとに、注入方法を検討します。" },
      { no: "03", title: "医師の診察のもと治療", text: "医師の診察のもと、治療の適応を判断します。" },
      { no: "04", title: "一人ひとりのご希望に合わせてご提案", text: "ご希望や顔立ちに合わせてご提案します。" },
    ],
    method: [
      { label: "分類", value: "INJECTABLES" },
      { label: "施術方法", value: "ヒアルロン酸注入" },
    ],
    price: [
      { label: "涙袋（両目）～1.0cc", price: "44,000円" },
      { label: "鼻 ～1.0cc", price: "44,000円" },
      { label: "唇 ～1.0cc", price: "66,000円" },
      { label: "顎（あご）～1.0cc", price: "66,000円" },
      { label: "首のしわ 1本（1.0cc）1本目", price: "66,000円" },
      { label: "ほうれい線（両側）～1.0cc", price: "66,000円" },
      { label: "口角（両側）～1.0cc", price: "66,000円" },
      { label: "小じわ ～1.0cc", price: "66,000円" },
      { label: "額（おでこ）6.0～10.0cc", price: "275,000円" },
      { label: "こめかみ 2.0～4.0cc", price: "66,000円／1cc" },
    ],
    flow,
    downtimeEn: "DOWNTIME",
    downtimeJp: "ダウンタイム",
    downtime,
    riskEn: "RISK / PRECAUTIONS",
    riskJp: "リスク・注意事項",
    riskParagraphs,
    faqs: [
      {
        q: "どの部位に施術できますか？",
        a: "本ページに掲載している部位を中心にご案内しています。実際の適応は診察時にご確認ください。",
      },
      {
        q: "どのくらい注入しますか？",
        a: "注入量は施術部位や顔立ち、ご希望などによって異なります。診察時にご案内します。",
      },
      {
        q: "どの製剤を使いますか？",
        a: "使用する製剤については、診察時に治療内容とあわせてご案内します。",
      },
      {
        q: "他の部位も同時に相談できますか？",
        a: "複数の部位についても、診察時にあわせてご相談いただけます。",
      },
      {
        q: "当日に施術できますか？",
        a: "ご予約状況や診察内容によって異なります。まずはご相談ください。",
      },
    ],
    related: [
      { label: "注入治療について", to: "/treatment/injectables" },
      { label: "ボトックスについて", to: "/injectables/botox" },
      { label: "注入治療の料金を見る", to: "/price?tab=injectables" },
    ],
    ctaHeading: "顔立ちやバランスについて、まずはご相談ください。",
    ctaLineLabel: "ヒアルロン酸についてLINEで相談",
  },
];

export function getInjectablesDetail(slug: string) {
  return injectablesDetails.find((page) => page.slug === slug);
}